<template>
  <Transition name="fade">
    <Dialog
      v-if="changeFactoryModal"
      title="Update factory"
      :showCloseButton="true"
      :showFooter="true"
      :showSaveButton="true"
      :showCancelButton="true"
      saveButtonText="Update"
      cancelButtonText="Cancel"
      :sm="true"
      @closeEvent="closeEvent"
      @cancelEvent="closeEvent"
      @saveEvent="onSave"
      :saveButtonProps="{
        disabled: !isFormValid,
      }"
    >
      <template #modal-content>
        <template v-if="change_factory_for_all">
          <p class="mb-2">Select Factory:</p>
          <Select
            append-to-body
            v-model="selectedFactoryLocal"
            id="change_factory"
            name="change_factory"
            placeholder="Select Factory"
            label="name"
            track-by="id"
            :options="factories"
          />
          <div class="mt-4">
            <p class="mb-2">Select item status:</p>
            <Select
              append-to-body
              v-model="selectedOrderStatus"
              :options="moveOrderOptions"
              placeholder="Select Updated Order Status"
              track-by="value"
              label="text"
            />
          </div>
        </template>
        <template v-else-if="order && !change_factory_for_all">
          <div
            :class="factory_product_index == 0 ? 'mt-1' : 'mt-5'"
            v-for="(factory_product, factory_product_index) in order.factory_products"
            :key="factory_product.id"
          >
            <div>
              <p class="mb-2">
                {{
                  factory_product.is_new_product
                    ? factory_product.new_product_name
                    : factory_product.product_name
                }}
                - Move this product to:
                <template v-if="factory_product.reorder_data">
                  {{
                    `(Reorder from ${factory_product.reorder_data.order_number}: made at: ${factory_product.product_last_factory})`
                  }}
                </template>
              </p>
              <Select
                append-to-body
                v-model="factory_product.selected_factory_id"
                :options="factories"
                :placeholder="order.factory_name"
                track-by="id"
                label="name"
              />
            </div>
            <div class="mt-4">
              <p class="mb-2">Select item status:</p>
              <Select
                append-to-body
                v-model="factory_product.selected_status"
                placeholder="Select Updated Order Status"
                :options="moveOrderOptions"
                track-by="value"
                label="text"
              />
            </div>
          </div>
        </template>
        <template v-if="show_all_items_option">
          <input type="checkbox" v-model="change_factory_for_all" class="mt-3" id="applyAll" />
          <label for="applyAll">Apply to all items</label>
        </template>
        <div v-if="!isFormValid" class="text-[red] mt-2">
          Products assigned to the same factory must have the same status.
        </div>
      </template>
    </Dialog>
  </Transition>
</template>
<script lang="ts" setup>
import Dialog from '@/components/Shared/Dialog.vue';
import Select from '@/components/Shared/Select.vue';
import { showToast } from '@/composables/swal';
import { handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import { DashboardItem, Factory, Order } from '@/models';
import { useMainStore } from '@/store/index';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
const props = defineProps<{
  currentTab: string;
  order_item: any;
}>();
const show_all_items_option = ref<boolean>(false);
const selectedFactoryLocal = ref<Factory | null | undefined>();
const selectedOrderLocal = ref<number | Order | null>(null);
const order = ref<DashboardItem | null>(null);
const selectedOrderId = ref<number | Order | null>(null);
const changeFactoryModal = ref<boolean>(false);

// const QUOTEREQUESTED = 'quote_requested';
// const QUOTEUPDATED = 'quote_updated';
// const QUOTEPROVIDED = 'quote_provided';
// const QUOTEREJECTED = 'quote_rejected';

const FACTORYREVIEW = 'submitted_for_factory_review';
const ORDERAPPROVE = 'order_approve';
const ORDERCANCEL = 'order_cancel';
const CUSTOMERREVIEW = 'submitted_for_customer_review';
// const CUSTOMERAPPROVED = 'customer_approved';
// const CUSTOMERREJECTED = 'customer_rejected';
const ORDERINPRODUCTION = 'in_production';
const ORDERSHIPPED = 'shipped';
const ORDERCOMPLETED = 'completed';

const change_factory_for_all = ref<boolean>(false);

const emit = defineEmits(['close', 'closeMenu']);

const selectedFactory = ref<Factory | null | undefined>(null);
const selectedOrderStatus = ref<Factory | null | undefined>(null);
const store = useMainStore();
const { factories } = storeToRefs(store);

const moveOrderOptions = [
  { value: null, text: 'Please select an option' },
  { value: FACTORYREVIEW, text: 'Order Created' },
  { value: ORDERAPPROVE, text: 'Mark to factory' },
  { value: ORDERCANCEL, text: 'Order Cancelled' },
  { value: CUSTOMERREVIEW, text: 'Customer Review' },
  { value: ORDERINPRODUCTION, text: 'In Production' },
  { value: ORDERSHIPPED, text: 'Order Shipped' },
  { value: ORDERCOMPLETED, text: 'Order Completed' },
];

const areStatusesConsistentForSameFactory = () => {
  if (!order.value || !order.value.factory_products) return true;
  const factoryStatusMap = new Map();
  for (const product of order.value.factory_products) {
    const factoryId = product.selected_factory_id?.id;
    const status = product.selected_status?.value;

    if (factoryId === null || status === null) continue;

    if (factoryStatusMap.has(factoryId)) {
      if (factoryStatusMap.get(factoryId) !== status) {
        return false;
      }
    } else {
      factoryStatusMap.set(factoryId, status);
    }
  }

  return true;
};

const isFormValid = computed(() => areStatusesConsistentForSameFactory());

const closeEvent = () => {
  emit('close');
  emit("closeMenu")
  changeFactoryModal.value = false;
};
const validateFactoryProductWithStatus = (payload: any) => {
  const productFactoryIds = payload.product_factory_ids;
  const factoryStatusMap: any = {};

  for (const productId in productFactoryIds) {
    const { factory_id, order_status } = productFactoryIds[productId];

    // Check if factory_id or order_status is null
    if (factory_id === null && order.value) {
      const product = order.value.factory_products.find(
        (factory_product) => factory_product.id === productId
      );
      return {
        valid: false,
        message: `Factory is not selected for product ${product?.product_name}.`,
      };
    }

    if (order_status === null && order.value) {
      const product = order.value.factory_products.find(
        (factory_product) => factory_product.id === productId
      );
      return {
        valid: false,
        message: `Order Status is not selected for product ID ${product?.product_name}.`,
      };
    }

    // Ensure the same factory_id has consistent order_status
    if (factoryStatusMap[factory_id]) {
      if (factoryStatusMap[factory_id] !== order_status) {
        const currentFactory = factories.value.find((factory) => factory.id === factory_id);
        return {
          valid: false,
          message: `Order status for same factory ${currentFactory?.name} should be same.`,
        };
      }
    } else {
      factoryStatusMap[factory_id] = order_status;
    }
  }

  return { valid: true, message: 'Validation passed.' };
};

const onSave = async (): Promise<void> => {
  if (order.value) {
    let payload: any = {
      order_id: order.value.order_id,
      order_item_id: order.value.id,
      product_factory_ids: {},
    };

    if (change_factory_for_all.value && selectedOrderLocal.value) {
      order.value.factory_products.forEach((product) => {
        payload.product_factory_ids[product.id] = {
          factory_id: selectedFactory.value ? selectedFactory.value.id : null,
          order_status: selectedOrderStatus.value,
        };
      });
    } else {
      order.value.factory_products.forEach((product) => {
        payload.product_factory_ids[product.id] = {
          factory_id: product.selected_factory_id.id,
          order_status: product.selected_status?.value,
        };
      });
    }
    const validationCheckForFactoryProductWithStatus = validateFactoryProductWithStatus(payload);
    if (!validationCheckForFactoryProductWithStatus.valid) {
      showToast(validationCheckForFactoryProductWithStatus.message, 'error');
      return;
    }

    try {
      store.updateOrderFactory(payload).then(async (res) => {
        if (res) {
          emit('close', true);
          changeFactoryModal.value = false;
        }
      });
    } catch (e: any) {
      handleResponseException(e);
      emit('close');
      changeFactoryModal.value = false;
    } finally {
      emit("closeMenu")
    }
  }
};
const showUpdateFactoryModal = () => {
  const item = props.order_item as DashboardItem;
  if (factories.value.length > 0) {
    let factory = factories.value.find((factory: Factory) => item.factory_id === factory.id);
    order.value = JSON.parse(JSON.stringify(item));
    order.value = {
      ...order.value,
      factory_products: order.value
        ? order.value.factory_products?.map((product) => ({
            ...product,
            selected_factory_id: product.factory_id,
            selected_status: null,
          }))
        : [],
    } as DashboardItem;
    selectedOrderId.value = item.order_id;
    selectedFactory.value = factory;
    if (item.factory_products?.length === 1) {
      show_all_items_option.value = false;
    }
    changeFactoryModal.value = true;
  } else {
    emit('close');
  }
};
onMounted(() => {
  showUpdateFactoryModal();
});
</script>
<style scoped lang="scss"></style>
